import type React from 'react';
import type { Key } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';

import { AppErrorCodeEnum } from '../../../enums/AppErrorCodeEnum';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import type File from '../../../interfaces/File';
import type { GetFilesPayload } from '../../../interfaces/FileApiService';
import { type FileActionKey, isLockedPasswordFolder } from '../../../lib/fileActions';
import { useAppStore } from '../../../store/store';
import { handleError } from '../../../utils/errorsHandler';
import type { UseFilesActionsEvents } from '../../useFilesActions';
import type { OpenFileSafelyOptions } from '../../useOpenFile/useOpenFile';
import { getPartialFiltersBasedOnTable } from '../tableFilters';

/**
 * Dependencies required by {@link useFilesMenuAction}.
 *
 * This interface encapsulates handlers, state setters and context required
 * to execute actions from the files toolbar or row context menu.
 */
export interface UseFilesMenuDeps {
  // Handlers for signaling changes to file data
  onFilesCreated: (newFiles: File[]) => void;
  onFilesUpdated: (updatedFiles: File[]) => void;
  onFilesDeleted: (removedFiles: File[]) => void;

  // Current file/folder context
  files: File[];
  parentFolder: File | null;
  table: FilesTableEnum;
  filters: Pick<GetFilesPayload, 'orderAsc' | 'orderColumn' | 'parent'>;

  // UI and utility dependencies
  message: MessageInstance;
  t: TFunction;
  isInFileRoute: (table: FilesTableEnum) => boolean;
  setIsNavigating: (v: boolean) => void;

  // Core file/folder actions (navigation and primary operations)
  openFolderOrRequestPassword: (record: File) => void;
  openFileSafely: (
    file: File,
    onBlankPageOrOptions?: boolean | OpenFileSafelyOptions,
    skipLocalDefaultOpening?: boolean,
  ) => boolean;
  copyFileLinkToClipboard: (file: File) => void;
  handleOpenShortcutOriginalFileLocation: (file: File) => void;
  handleDownloadFiles: (files: File[]) => void;
  handleDownloadEncryptedFile: (file: File) => void;

  // File mutation actions
  handleRenameFile: (file: File, events?: UseFilesActionsEvents) => void;
  handleFavouriteFileChange: (
    files: File[],
    isFavourite: boolean,
    events?: UseFilesActionsEvents,
  ) => void;
  handleCreateFileShortcut: (file: File, events?: UseFilesActionsEvents) => void;
  handleCreateFilesDuplicate: (files: File[], events?: UseFilesActionsEvents) => void;
  handleReplaceFile: (file: File, events?: UseFilesActionsEvents) => void;
  handleMoveFiles: (
    files: File[],
    parentFolder: File | null,
    externalFilters?: Partial<GetFilesPayload>,
    events?: UseFilesActionsEvents,
  ) => void;
  handleConvertToPdf: (
    file: File,
    parentFolder: File | null,
    externalFilters?: Partial<GetFilesPayload>,
    events?: UseFilesActionsEvents,
  ) => void;
  handleHideFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleUnhideFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleDeleteFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handlePermanentlyDeleteFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleRestoreFilesFromTrash: (files: File[], events?: UseFilesActionsEvents) => void;
  handleShareFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleEditFileTags: (file: File, events?: UseFilesActionsEvents) => void;
  handleSellFile: (file: File, events?: UseFilesActionsEvents) => void;

  // File security & sharing
  handleSetFilePassword: (file: File, events?: UseFilesActionsEvents) => void;
  handleEditFilePassword: (file: File, events?: UseFilesActionsEvents) => void;
  handleCreatePublicLink: (file: File, events?: UseFilesActionsEvents) => void;
  handleRemoveFilePassword: (file: File, events?: UseFilesActionsEvents) => void;
  handleLockFiles: (files: File[], events?: UseFilesActionsEvents) => void;
  handleUnlockFolder: (file: File, events?: UseFilesActionsEvents) => void;
  getFilePasswordSession: (fileId: number) => string | null | undefined;

  // AI & smart actions
  handleAiClassifyFile: (files: File[], events?: UseFilesActionsEvents) => void;
  handleAiSummaryFile: (file: File, events?: UseFilesActionsEvents) => void;
  handleAiDocumentChatFile: (file: File, events?: UseFilesActionsEvents) => void;

  // Miscellaneous actions
  handleOpenFileLocally: (file: File, events?: UseFilesActionsEvents) => void;
  handleKeepFileOffline: (files: File[], events?: UseFilesActionsEvents) => void;
  openRightPanel: () => void;
  handleMarkFilesAsRead: (files: File[], events?: UseFilesActionsEvents) => void;
  handleMarkFilesAsUnread: (files: File[], events?: UseFilesActionsEvents) => void;
  handleEmptyTrash: (events?: UseFilesActionsEvents) => void;
  handleOpenFileFromTrash: (file: File, events?: UseFilesActionsEvents) => void;

  // State setters for managing file selection and listing
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  setTotalFiles: React.Dispatch<React.SetStateAction<number>>;
  setSelectedTableKeys: React.Dispatch<React.SetStateAction<Key[]>>;
  setPrependFileData: React.Dispatch<React.SetStateAction<File | null>>;
  setFilesOfflinePollingBump: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Returns the `handleFileMenuAction` function. This handler maps a FileMenuActionEnum
 * action to the corresponding handler function, given a selection of files and the
 * current application/table context.
 *
 * @param deps Context and handler dependencies (see UseFilesMenuDeps)
 * @returns Object containing the stable handleFileMenuAction function
 */
export function useFilesMenuAction(deps: UseFilesMenuDeps) {
  /**
   * Invokes the correct file action based on action type and selected file IDs.
   * Handles single and multi-file flows, as well as table-specific logic (e.g., Trash).
   *
   * @param fileIds Array of selected file IDs
   * @param action  The menu action requested
   */
  const handleFileMenuAction = (fileIds: number[], action: FileActionKey) => {
    // Destructure dependencies/handlers from hook params
    const {
      files,
      setIsNavigating,
      openFolderOrRequestPassword,
      isInFileRoute,
      openFileSafely,
      handleOpenFileFromTrash,
      onFilesDeleted,
      copyFileLinkToClipboard,
      message,
      t,
      handleOpenShortcutOriginalFileLocation,
      handleDownloadFiles,
      handleDownloadEncryptedFile,
      handleRenameFile,
      onFilesUpdated,
      handleFavouriteFileChange,
      handleCreateFileShortcut,
      onFilesCreated,
      handleCreateFilesDuplicate,
      handleReplaceFile,
      handleMoveFiles,
      handleConvertToPdf,
      parentFolder,
      table,
      filters,
      handleHideFiles,
      handleUnhideFiles,
      handleDeleteFiles,
      handlePermanentlyDeleteFiles,
      handleRestoreFilesFromTrash,
      handleShareFiles,
      handleEditFileTags,
      handleSellFile,
      handleSetFilePassword,
      handleEditFilePassword,
      handleCreatePublicLink,
      handleRemoveFilePassword,
      handleLockFiles,
      getFilePasswordSession,
      handleUnlockFolder,
      handleAiClassifyFile,
      handleAiSummaryFile,
      handleAiDocumentChatFile,
      handleOpenFileLocally,
      handleKeepFileOffline,
      openRightPanel,
      handleMarkFilesAsRead,
      handleMarkFilesAsUnread,
      handleEmptyTrash,
      setFiles,
      setTotalFiles,
      setSelectedTableKeys,
      setPrependFileData,
      setFilesOfflinePollingBump,
    } = deps;

    // Get the first selected File object, if exists
    const foundFile = files.find((file) => file.id === fileIds[0]);

    switch (action) {
      /**
       * Open a file or folder (special handling for Trash route).
       */
      case FileMenuActionEnum.Open:
        if (fileIds.length === 1 && foundFile) {
          if (foundFile.isFolder) {
            if (files.length > 0) {
              setIsNavigating(true);
            }
            openFolderOrRequestPassword(foundFile);
          } else {
            if (!isInFileRoute(FilesTableEnum.Trash)) {
              // Secrets: force full viewer (click path uses modal instead).
              openFileSafely(foundFile, {
                onBlankPage: true,
                skipLocalDefaultOpening: true,
                forceViewer: true,
              });
            } else {
              // Special Trash route: open deleted file from trash
              handleOpenFileFromTrash(foundFile, {
                onFilesDeleted: onFilesDeleted,
              });
            }
          }
        }
        break;

      /**
       * Open file with forced online viewer (special handling for Trash).
       */
      case FileMenuActionEnum.OpenFileOnline:
        if (fileIds.length === 1 && foundFile) {
          if (foundFile.isFolder) {
            if (files.length > 0) {
              setIsNavigating(true);
            }
            openFolderOrRequestPassword(foundFile);
          } else {
            if (!isInFileRoute(FilesTableEnum.Trash)) {
              openFileSafely(foundFile, true, true);
            } else {
              handleOpenFileFromTrash(foundFile, {
                onFilesDeleted: onFilesDeleted,
              });
            }
          }
        }
        break;

      /**
       * Copy a file link to the clipboard and show feedback.
       */
      case FileMenuActionEnum.CopyLink:
        if (fileIds.length === 1 && foundFile) {
          copyFileLinkToClipboard(foundFile);
          message.success(t('common.linkCopied'));
        }
        break;

      /**
       * Open the location of the original file for a shortcut.
       */
      case FileMenuActionEnum.OpenShortcutOriginalFileLocation:
        if (fileIds.length === 1 && foundFile) {
          handleOpenShortcutOriginalFileLocation(foundFile);
        }
        break;

      /**
       * Download the selected files.
       */
      case FileMenuActionEnum.Download: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleDownloadFiles(foundFiles);
        }
        break;
      }

      /**
       * Download an encrypted file (single-file only).
       */
      case FileMenuActionEnum.DownloadEncrypted:
        if (fileIds.length === 1 && foundFile) {
          handleDownloadEncryptedFile(foundFile);
        }
        break;

      /**
       * Trigger renaming dialog for a file (single-file only).
       */
      case FileMenuActionEnum.Rename:
        if (fileIds.length === 1 && foundFile) {
          handleRenameFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Mark files as favorite(s).
       */
      case FileMenuActionEnum.AddToFavorites: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleFavouriteFileChange(foundFiles, true, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      }

      /**
       * Remove favorite status from files.
       */
      case FileMenuActionEnum.RemoveFromFavorites: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleFavouriteFileChange(foundFiles, false, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      }

      /**
       * Create a shortcut for a file (single-file only).
       */
      case FileMenuActionEnum.CreateShortcut:
        if (fileIds.length === 1 && foundFile) {
          handleCreateFileShortcut(foundFile, {
            onFilesCreated: onFilesCreated,
          });
        }
        break;

      /**
       * Duplicate selected file(s) (multi-file supported).
       */
      case FileMenuActionEnum.Duplicate: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        if (foundFiles.length > 0) {
          handleCreateFilesDuplicate(foundFiles, {
            onFilesCreated: onFilesCreated,
          });
        }
        break;
      }

      /**
       * Replace file contents (single-file only).
       */
      case FileMenuActionEnum.Replace:
        if (fileIds.length === 1 && foundFile) {
          handleReplaceFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Move selected files to another folder.
       */
      case FileMenuActionEnum.Move: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleMoveFiles(
          foundFiles,
          parentFolder,
          {
            ...getPartialFiltersBasedOnTable(table),
            orderAsc: filters.orderAsc,
            orderColumn: filters.orderColumn,
            ...(filters.parent ? { parent: filters.parent } : {}),
          },
          {
            onFilesDeleted: onFilesDeleted,
          },
        );
        break;
      }

      /**
       * Convert file to PDF.
       */
      case FileMenuActionEnum.ConvertToPdf:
        if (fileIds.length === 1 && foundFile) {
          handleConvertToPdf(
            foundFile,
            parentFolder,
            {
              ...getPartialFiltersBasedOnTable(table),
              orderAsc: filters.orderAsc,
              orderColumn: filters.orderColumn,
              ...(filters.parent ? { parent: filters.parent } : {}),
            },
            {
              onFilesCreated: onFilesCreated,
            },
          );
        }
        break;

      /**
       * Hide selected files.
       */
      case FileMenuActionEnum.Hide: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleHideFiles(foundFiles, {
          onFilesDeleted: onFilesDeleted,
        });
        break;
      }

      /**
       * Unhide selected files.
       */
      case FileMenuActionEnum.Unhide: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleUnhideFiles(foundFiles, {
          onFilesDeleted: onFilesDeleted,
        });
        break;
      }

      /**
       * Move selected files to Trash.
       */
      case FileMenuActionEnum.Delete: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleDeleteFiles(foundFiles, {
          onFilesDeleted: onFilesDeleted,
        });
        break;
      }

      /**
       * Permanently delete selected files.
       */
      case FileMenuActionEnum.DeletePermanently: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handlePermanentlyDeleteFiles(foundFiles, {
          onFilesDeleted: onFilesDeleted,
        });
        break;
      }

      /**
       * Restore files from Trash.
       */
      case FileMenuActionEnum.RestoreFromTrash: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleRestoreFilesFromTrash(foundFiles, {
          onFilesDeleted: onFilesDeleted,
        });
        break;
      }

      /**
       * Share files (with per-file updates callback).
       * Locked password folders must be unlocked first (same modal as Unlock).
       */
      case FileMenuActionEnum.Share: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        const lockedFolders = foundFiles.filter((f) =>
          isLockedPasswordFolder(f, getFilePasswordSession),
        );
        if (lockedFolders.length > 1) {
          handleError(AppErrorCodeEnum.CannotUnlockMultiplePasswordProtected);
        } else if (lockedFolders.length === 1) {
          const folderToUnlock = lockedFolders[0];
          if (folderToUnlock) {
            handleUnlockFolder(folderToUnlock, {
              onFilesUpdated: () => {
                handleShareFiles(foundFiles, { onFilesUpdated });
              },
            });
          }
        } else {
          handleShareFiles(foundFiles, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;
      }

      /**
       * Edit tags for a single file.
       */
      case FileMenuActionEnum.Tags:
        if (fileIds.length === 1 && foundFile) {
          handleEditFileTags(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Mark a single file as 'for sale'.
       */
      case FileMenuActionEnum.SellFile:
        if (fileIds.length === 1 && foundFile) {
          handleSellFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Add password to a file (single-file only).
       */
      case FileMenuActionEnum.SetFilePassword:
        if (fileIds.length === 1 && foundFile) {
          handleSetFilePassword(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Edit file password (single-file only).
       */
      case FileMenuActionEnum.EditPassword:
        if (fileIds.length === 1 && foundFile) {
          handleEditFilePassword(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Create a public link share for a file.
       */
      case FileMenuActionEnum.PublicLink:
        if (fileIds.length === 1 && foundFile) {
          handleCreatePublicLink(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Remove file password protection (single-file only).
       */
      case FileMenuActionEnum.RemovePassword:
        if (fileIds.length === 1 && foundFile) {
          handleRemoveFilePassword(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Lock selected files.
       */
      case FileMenuActionEnum.Lock: {
        const filesToLock = files.filter((file) => fileIds.includes(file.id));
        if (filesToLock.length > 0) {
          handleLockFiles(filesToLock, { onFilesUpdated });
        }
        break;
      }

      /**
       * Unlock password-protected folder (only one at a time!).
       */
      case FileMenuActionEnum.Unlock: {
        const selectedFilesForUnlock = files.filter((f) => fileIds.includes(f.id));
        // Only allow unlocking one locked folder at a time
        const lockedFolders = selectedFilesForUnlock.filter((f) =>
          isLockedPasswordFolder(f, getFilePasswordSession),
        );
        if (lockedFolders.length > 1) {
          handleError(AppErrorCodeEnum.CannotUnlockMultiplePasswordProtected);
        } else if (lockedFolders.length === 1) {
          const folderToUnlock = lockedFolders[0];
          if (folderToUnlock) {
            handleUnlockFolder(folderToUnlock, { onFilesUpdated });
          }
        }
        break;
      }

      /**
       * Smart/AI: classify selected files (already classified ones are re-classified).
       */
      case FileMenuActionEnum.Classify: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleAiClassifyFile(foundFiles, {
          onFilesUpdated: onFilesUpdated,
        });
        break;
      }

      /**
       * Smart/AI: generate a summary for a file.
       */
      case FileMenuActionEnum.ArtificialIntelligenceSummary:
        if (fileIds.length === 1 && foundFile) {
          handleAiSummaryFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Smart/AI: Chat about a document (single-file only).
       */
      case FileMenuActionEnum.ArtificialIntelligenceDocumentChat:
        if (fileIds.length === 1 && foundFile) {
          handleAiDocumentChatFile(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Open file using local application (single-file only).
       */
      case FileMenuActionEnum.OpenFileLocally:
        if (fileIds.length === 1 && foundFile) {
          handleOpenFileLocally(foundFile, {
            onFilesUpdated: onFilesUpdated,
          });
        }
        break;

      /**
       * Keep selected files available offline.
       */
      case FileMenuActionEnum.KeepOffline: {
        const foundFiles = files.filter((file) => fileIds.includes(file.id));
        handleKeepFileOffline(foundFiles, {
          onFilesUpdated: (updatedFiles) => {
            onFilesUpdated(updatedFiles);
            // Bump polling state to refresh offline status UI
            setFilesOfflinePollingBump((prev) => prev + 1);
          },
        });
        break;
      }

      /**
       * Show file or folder details panel.
       */
      case FileMenuActionEnum.Details:
        useAppStore.getState().setRightPanelKind('detail');
        openRightPanel();
        break;

      /**
       * Mark selected files as 'read'.
       */
      case FileMenuActionEnum.MarkAsRead: {
        const foundFilesForRead = files.filter((file) => fileIds.includes(file.id));
        handleMarkFilesAsRead(foundFilesForRead, { onFilesUpdated });
        break;
      }

      /**
       * Mark selected files as 'unread', and update prepended file preview if present.
       */
      case FileMenuActionEnum.MarkAsUnread: {
        const foundFilesForUnread = files.filter((file) => fileIds.includes(file.id));
        handleMarkFilesAsUnread(foundFilesForUnread, {
          onFilesUpdated: (updated) => {
            onFilesUpdated(updated);
            // If the 'prepending' file is in updated, recompute "isUnRead"
            setPrependFileData((prev) => {
              if (!prev) return prev;
              const hit = updated.find((f) => f.id === prev.id);
              return hit ? { ...prev, isUnRead: true } : prev;
            });
          },
        });
        break;
      }

      /**
       * Empty the entire Trash; resets file states and selection.
       */
      case FileMenuActionEnum.EmptyTrash:
        handleEmptyTrash({
          onFilesDeleted: () => {
            setFiles([]);
            setTotalFiles(0);
            setSelectedTableKeys([]);
          },
        });
        break;
    }
  };

  return { handleFileMenuAction };
}
